import React, {useEffect, useState} from 'react';
import {
  Button, TextField, IconButton, Icon, MenuItem, Chip,
} from '@material-ui/core';
import {Field, FieldArray, Form, Formik} from 'formik';
import * as yup from 'yup';
import {aircraftFormStyles} from './aircrafts.styles';
import {toggleQuickPanel} from 'app/main/shared-components/quick-panel/store';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from 'app/store';
import {createAircraft} from '../store/aircrafts/create-aircraft.slice';
import {updateAircraft} from '../store/aircrafts/update-aircraft.slice';
import LoadingButton from 'app/main/shared-components/loading-button/LoadingButton';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {AircraftDefinitionRequest, SeatClassMapping, BlockedSeat, AircraftDefinitionResponse} from 'app/types/service-types/AircraftTypes';
import LoadingForm from '../../shared-components/loading-form/LoadingForm';
import {fetchAllSeatClasses} from '../store/seat-classes/seat-classes.slice';
import {fetchAircraftById, clearSelectedAircraft} from '../store/aircrafts/aircrafts.slice';

const validationSchema = yup.object({
  code: yup.string()
    .required('Code is required')
    .min(3, 'Code must be at least 3 characters')
    .max(20, 'Code must be at most 20 characters')
    .matches(/^[A-Z0-9-]+$/, 'Code must contain only uppercase letters, numbers, and hyphens'),
  name: yup.string()
    .required('Name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name must be at most 100 characters'),
  totalRows: yup.number()
    .required('Total rows is required')
    .integer('Must be an integer')
    .min(1, 'Must be at least 1')
    .max(100, 'Must be at most 100'),
  seatColumns: yup.string()
    .required('Seat columns is required')
    .matches(/^[A-Z,]+$/, 'Must contain only uppercase letters (e.g. ABCDEF)'),
  layoutDescription: yup.string().notRequired(),
});

interface AircraftFormValues {
  code: string;
  name: string;
  totalRows: number;
  seatColumns: string;
  layoutDescription: string;
  seatClassMappings: SeatClassMapping[];
  exitRows: string;
  overWingRows: string;
  blockedSeats: BlockedSeat[];
}

const getInitialValues = (): AircraftFormValues => ({
  code: '',
  name: '',
  totalRows: 30,
  seatColumns: 'ABCDEF',
  layoutDescription: '',
  seatClassMappings: [],
  exitRows: '',
  overWingRows: '',
  blockedSeats: [],
});

const AircraftForm = () => {
  const classes = aircraftFormStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  let {params} = useRouteMatch();

  const aircraftsState = useSelector((state: AppState) => state.configurations.aircrafts.aircrafts);
  const {selectedAircraft, detailLoading} = useSelector((state: AppState) => state.configurations.aircrafts.aircrafts);
  const seatClassesState = useSelector((state: AppState) => state.configurations.seatClasses.seatClasses);
  const createState = useSelector((state: AppState) => state.configurations.aircrafts.createAircraft);
  const updateState = useSelector((state: AppState) => state.configurations.aircrafts.updateAircraft);

  const [loading, setLoading] = useState(true);
  const [formValues, setFormValues] = useState<AircraftFormValues>(getInitialValues());
  const [componentTitle, setComponentTitle] = useState('Register');

  // Helper function to extract seat class mappings from seat definitions
  const extractSeatClassMappings = (aircraft: AircraftDefinitionResponse): SeatClassMapping[] => {
    if (!aircraft.seatDefinitions || aircraft.seatDefinitions.length === 0) return [];
    
    const mappings: Map<string, {seatClassId: string, rows: Set<number>}> = new Map();
    
    aircraft.seatDefinitions.forEach(seat => {
      const key = seat.seatClass.id;
      if (!mappings.has(key)) {
        mappings.set(key, {seatClassId: key, rows: new Set()});
      }
      mappings.get(key)!.rows.add(seat.rowNumber);
    });
    
    const result: SeatClassMapping[] = [];
    mappings.forEach(mapping => {
      const sortedRows = Array.from(mapping.rows).sort((a, b) => a - b);
      let fromRow = sortedRows[0];
      let toRow = sortedRows[0];
      
      for (let i = 1; i < sortedRows.length; i++) {
        if (sortedRows[i] === toRow + 1) {
          toRow = sortedRows[i];
        } else {
          result.push({seatClassId: mapping.seatClassId, fromRow, toRow});
          fromRow = sortedRows[i];
          toRow = sortedRows[i];
        }
      }
      result.push({seatClassId: mapping.seatClassId, fromRow, toRow});
    });
    
    return result;
  };

  // Helper function to extract blocked seats
  const extractBlockedSeats = (aircraft: AircraftDefinitionResponse): BlockedSeat[] => {
    if (!aircraft.seatDefinitions) return [];
    return aircraft.seatDefinitions
      .filter(seat => seat.blocked)
      .map(seat => ({row: seat.rowNumber, column: seat.columnLetter}));
  };

  // Helper function to extract special rows
  const extractSpecialRows = (aircraft: AircraftDefinitionResponse, field: 'nearExit' | 'overWing'): string => {
    if (!aircraft.seatDefinitions) return '';
    const rows = new Set<number>();
    aircraft.seatDefinitions.forEach(seat => {
      if (seat[field]) rows.add(seat.rowNumber);
    });
    return Array.from(rows).sort((a, b) => a - b).join(', ');
  };

  useEffect(() => {
    dispatch(fetchAllSeatClasses({status: 'ACTIVE', size: 9999}));
    
    // @ts-ignore
    if (params?.id) {
      setComponentTitle('Update');
      // @ts-ignore
      dispatch(fetchAircraftById(params.id));
    } else {
      setLoading(false);
    }
    
    return () => {
      dispatch(clearSelectedAircraft());
    };
  }, []);

  useEffect(() => {
    if (selectedAircraft && params) {
      setFormValues({
        code: selectedAircraft.code,
        name: selectedAircraft.name,
        totalRows: selectedAircraft.totalRows,
        seatColumns: selectedAircraft.seatColumns ? selectedAircraft.seatColumns.replace(/,/g, '') : '',
        layoutDescription: selectedAircraft.layoutDescription || '',
        seatClassMappings: extractSeatClassMappings(selectedAircraft),
        exitRows: extractSpecialRows(selectedAircraft, 'nearExit'),
        overWingRows: extractSpecialRows(selectedAircraft, 'overWing'),
        blockedSeats: extractBlockedSeats(selectedAircraft),
      });
      setLoading(false);
    }
  }, [selectedAircraft]);

  if (loading) return <LoadingForm/>;

  const parseNumberArray = (str: string): number[] => {
    if (!str.trim()) return [];
    return str.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
  };

  const formatSeatColumns = (input: string): string => {
    // Convert "ABCDEF" to "A,B,C,D,E,F" for the backend
    const letters = input.replace(/,/g, '').split('');
    return letters.join(',');
  };

  const onSubmitFormRequest = (data: AircraftFormValues) => {
    const body: AircraftDefinitionRequest = {
      code: data.code,
      name: data.name,
      totalRows: data.totalRows,
      seatColumns: formatSeatColumns(data.seatColumns),
      layoutDescription: data.layoutDescription || undefined,
      seatClassMappings: data.seatClassMappings.length > 0 ? data.seatClassMappings : undefined,
      exitRows: parseNumberArray(data.exitRows).length > 0 ? parseNumberArray(data.exitRows) : undefined,
      overWingRows: parseNumberArray(data.overWingRows).length > 0 ? parseNumberArray(data.overWingRows) : undefined,
      blockedSeats: data.blockedSeats,
    };

    // @ts-ignore
    if (params?.id) {
      dispatch(updateAircraft({
        // @ts-ignore
        id: params.id,
        body,
        onSuccess: () => dispatch(toggleQuickPanel())
      }));
    } else {
      dispatch(createAircraft({body}));
    }
  };

  const {seatClasses} = seatClassesState;

  return (
    <div className={classes.formWrapper}>
      <div className={classes.formHeader}>
        <h1 style={{color: '#0D3B66'}}>{componentTitle} Aircraft:</h1>
      </div>
      <Formik
        validateOnChange={true}
        validationSchema={validationSchema}
        initialValues={formValues}
        enableReinitialize
        onSubmit={onSubmitFormRequest}
      >
        {({values, errors, touched, setFieldValue}) => (
          <Form>
            {/* Basic Info */}
            <div className={classes.row}>
              <Field
                variant='outlined'
                name='code'
                label='Aircraft Code'
                placeholder='e.g., B737-800'
                as={TextField}
                required
                error={touched.code && Boolean(errors.code)}
                helperText={touched.code && errors.code}
              />
            </div>
            <div className={classes.row}>
              <Field
                variant='outlined'
                name='name'
                label='Aircraft Name'
                placeholder='e.g., Boeing 737-800'
                as={TextField}
                required
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
            </div>
            <div className={classes.row}>
              <Field
                variant='outlined'
                name='totalRows'
                label='Total Rows'
                type='number'
                as={TextField}
                required
                error={touched.totalRows && Boolean(errors.totalRows)}
                helperText={touched.totalRows && errors.totalRows}
              />
              <Field
                variant='outlined'
                name='seatColumns'
                label='Seat Columns'
                placeholder='e.g., ABCDEF'
                as={TextField}
                required
                error={touched.seatColumns && Boolean(errors.seatColumns)}
                helperText={touched.seatColumns && errors.seatColumns}
              />
            </div>
            <div className={classes.row}>
              <Field
                variant='outlined'
                name='layoutDescription'
                label='Layout Description'
                placeholder='e.g., 3-3 configuration'
                as={TextField}
              />
            </div>

            {/* Seat Class Mappings */}
            <div className={classes.sectionTitle}>Seat Class Mappings</div>
            <FieldArray name="seatClassMappings">
              {({push, remove}) => (
                <div>
                  {values.seatClassMappings.map((mapping, index) => (
                    <div key={index} className={classes.mappingRow}>
                      <TextField
                        select
                        variant='outlined'
                        size='small'
                        label='Seat Class'
                        value={mapping.seatClassId}
                        onChange={(e) => setFieldValue(`seatClassMappings.${index}.seatClassId`, e.target.value)}
                      >
                        {seatClasses.map((sc: any) => (
                          <MenuItem key={sc.id} value={sc.id}>
                            {sc.name} — ${sc.basePrice}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        variant='outlined'
                        size='small'
                        type='number'
                        label='From Row'
                        value={mapping.fromRow}
                        onChange={(e) => setFieldValue(`seatClassMappings.${index}.fromRow`, parseInt(e.target.value))}
                      />
                      <TextField
                        variant='outlined'
                        size='small'
                        type='number'
                        label='To Row'
                        value={mapping.toRow}
                        onChange={(e) => setFieldValue(`seatClassMappings.${index}.toRow`, parseInt(e.target.value))}
                      />
                      <IconButton size='small' onClick={() => remove(index)}>
                        <Icon color="error" fontSize="small">delete</Icon>
                      </IconButton>
                    </div>
                  ))}
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => push({seatClassId: '', fromRow: 1, toRow: 1})}
                    startIcon={<Icon>add</Icon>}
                    style={{marginTop: 4, textTransform: 'none'}}
                  >
                    Add Seat Class Mapping
                  </Button>
                </div>
              )}
            </FieldArray>

            {/* Exit & Over-Wing Rows */}
            <div className={classes.sectionTitle}>Special Rows</div>
            <div className={classes.row}>
              <Field
                variant='outlined'
                name='exitRows'
                label='Exit Rows (comma-separated)'
                placeholder='e.g., 10, 20'
                as={TextField}
              />
            </div>
            <div className={classes.row}>
              <Field
                variant='outlined'
                name='overWingRows'
                label='Over-Wing Rows (comma-separated)'
                placeholder='e.g., 12, 13, 14'
                as={TextField}
              />
            </div>

            {/* Blocked Seats */}
            <div className={classes.sectionTitle}>Blocked Seats</div>
            <FieldArray name="blockedSeats">
              {({push, remove}) => (
                <div>
                  <div className={classes.chipContainer}>
                    {values.blockedSeats.map((seat, index) => (
                      <Chip
                        key={index}
                        label={`${seat.row}${seat.column}`}
                        onDelete={() => remove(index)}
                        size="small"
                        color="default"
                      />
                    ))}
                  </div>
                  <div style={{display: 'flex', gap: 8, alignItems: 'center', marginTop: 4}}>
                    <TextField
                      variant='outlined'
                      size='small'
                      type='number'
                      label='Row'
                      id='blocked-row-input'
                      style={{width: 80}}
                    />
                    <TextField
                      variant='outlined'
                      size='small'
                      label='Col'
                      id='blocked-col-input'
                      style={{width: 80}}
                      inputProps={{maxLength: 1}}
                    />
                    <Button
                      size='small'
                      variant='outlined'
                      onClick={() => {
                        const rowEl = document.getElementById('blocked-row-input') as HTMLInputElement;
                        const colEl = document.getElementById('blocked-col-input') as HTMLInputElement;
                        const row = parseInt(rowEl?.value);
                        const col = colEl?.value?.toUpperCase();
                        if (row && col && /^[A-Z]$/.test(col)) {
                          push({row, column: col});
                          rowEl.value = '';
                          colEl.value = '';
                        }
                      }}
                      startIcon={<Icon>add</Icon>}
                      style={{textTransform: 'none'}}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </FieldArray>

            {/* Submit */}
            <div className={classes.row} style={{marginTop: 16}}>
              <LoadingButton
                type='submit'
                btnTitle='Submit'
                isLoading={createState.loading || updateState.loading}
              />
              <Button onClick={() => {
                dispatch(toggleQuickPanel());
                history.replace('/configurations/aircrafts');
              }}>
                Close
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AircraftForm;
